import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { InputSwitch } from 'primereact/inputswitch';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import RingilSidebar from '@components/ringil3/Sidebar/RingilSidebar';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecurityMode } from '@components/security/SecurityModeContext';
import SvgGrip from '@icons/grip-dots.svg?react';
import SvgClose from '@icons/close.svg?react';
import SvgPencil from '@icons/pencil.svg?react';
import SvgRingilAvatar from '@icons/ringil-avatar.svg?react';
import CustomTooltip from '@components/ringil3/Tooltip/CustomTooltip';
import type { FieldKey, ViewColumn } from '../views/types';
import { columnHeading, derivedColumnHeading, FieldMeta, FieldPreset, matchingPreset } from './columnModel';
import { fieldAccess, visibleColumns, visiblePresets } from './fieldAccess';
import {
  dropColumnOnGap,
  dropFieldOnColumn,
  dropFieldOnGap,
  isFieldShown,
  presetColumnId,
  presetTakesFrom,
  removeColumnFromSidebar,
  toggleField,
  togglePreset,
} from './columnSidebarOps';
import { MAX_COLUMN_TITLE_LENGTH, removeField, renameColumn } from '../views/columnOps';
import useColumnDnd from './useColumnDnd';
import InputSelect from '@components/ringil3/Input/InputSelect/InputSelect.tsx';
import type { PagingMode } from '../infiniteScroll';

interface ComponentProps {
  visible: boolean;
  onClose: () => void;
  /** The tab's working columns — the full ones, fields the user can't see included. */
  columns: ViewColumn[];
  /** "Uložit": the edited columns go into the tab's working copy (the tab itself is saved from the tab strip). */
  onApply: (columns: ViewColumn[]) => void;
  /** The columns Ringil gives a new tab; "Reset to default" puts them into the draft, and Save applies them. */
  defaultColumns: () => ViewColumn[];
  fields: FieldMeta[];
  presets: FieldPreset[];
  /** Sections of "Dostupné sloupce", in order; fields name theirs in `group`. */
  groups: { key: string; fallback: string }[];
  /**
   * Pages or infinite scroll, for a table that offers scrolling (absent: the choice isn't shown).
   * `cap` is the most rows scrolling loads, said under the choice.
   */
  paging?: { mode: PagingMode; cap: number; onApply: (mode: PagingMode) => void };
}

const row = 'flex items-center gap-2.5 bg-base-secondary rounded px-2 py-2 text-sm font-medium leading-5 text-content-primary';

/**
 * "Obsah tabulky" (Figma 21940:60140): the open tab's columns and the fields to add to them.
 *
 * "Pořadí sloupců" lists the columns as cards. Drag a card to reorder; drag a field onto a card to
 * stack it there, or between cards to give it a column of its own. "Dostupné sloupce" lists every
 * field and preset with a switch, and its rows can be dragged into the order list too. Fields the
 * user may not see are left out — except in security mode, where they show disabled and highlighted.
 *
 * A preset is not one value but Ringil's whole column: its row carries the Ringil mark and lists the
 * fields it brings, says which columns switching it on would break up, and a column card keeps the
 * mark for as long as it is still exactly that preset.
 */
const ColumnSidebar: React.FC<ComponentProps> = ({ visible, onClose, columns, onApply, defaultColumns, fields, presets, groups, paging }) => {
  const { tr } = useTranslationLgs();
  const { enabled: securityMode } = useSecurityMode();
  const [draft, setDraft] = useState<ViewColumn[]>(columns);
  const [pagingDraft, setPagingDraft] = useState<PagingMode>(paging?.mode ?? 'pages');
  // The column whose heading is being typed, and the text so far.
  const [renaming, setRenaming] = useState<{ id: string; text: string } | null>(null);
  const commitRename = () => {
    if (renaming) setDraft(current => renameColumn(current, renaming.id, renaming.text));
    setRenaming(null);
  };
  const dnd = useColumnDnd();

  // Each opening edits the tab's current columns, not what was left from the last time.
  useEffect(() => {
    if (visible) {
      setDraft(columns);
      setPagingDraft(paging?.mode ?? 'pages');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const isAllowed = useMemo(() => fieldAccess(fields), [fields]);
  const byKey = useMemo(() => new Map(fields.map(field => [field.key, field])), [fields]);
  const shownPresets = useMemo(() => visiblePresets(presets, isAllowed), [presets, isAllowed]);
  const shownColumns = visibleColumns(draft, isAllowed);
  const label = (key: FieldKey) => {
    const field = byKey.get(key);
    return field ? tr(field.header, field.headerDefault) : key;
  };
  const isRequired = (key: FieldKey) => !!byKey.get(key)?.required;

  const onDrop = (event: React.DragEvent) =>
    dnd.commitDrop(event, {
      onFieldToColumn: (key, target) => setDraft(current => dropFieldOnColumn(current, key, target, isAllowed)),
      onFieldToGap: (key, gap) => setDraft(current => dropFieldOnGap(current, key, gap, isAllowed)),
      onColumnToGap: (id, gap) => setDraft(current => dropColumnOnGap(current, id, gap, isAllowed)),
    });

  // A render function, not a component: a component declared here would remount on every drag-over
  // state change and could lose the drop.
  const gap = (index: number) => (
    <div
      key={`gap-${index}`}
      onDragOver={event => dnd.hoverGap(event, index)}
      onDrop={onDrop}
      className={clsx('-my-1 h-2 rounded transition-colors', dnd.gapIndex === index && 'bg-content-brand')}
      data-testid={`column-gap-${index}`}
    />
  );

  const removeButton = (onClick: () => void, disabled: boolean, title: string) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      title={title}
      className="ml-auto flex p-0 bg-transparent border-0 cursor-pointer text-content-brand disabled:cursor-not-allowed disabled:text-content-tertiary"
    >
      <SvgClose className="w-4 h-4" />
    </button>
  );

  // The Ringil mark: "this is Ringil's recommended column, not a single value".
  const ringilMark = (id: string, tooltip: string) => (
    <>
      <span id={id} className="inline-flex shrink-0" aria-label={tooltip} data-testid={id}>
        <SvgRingilAvatar className="w-4 h-4" />
      </span>
      <CustomTooltip targetSelector={`#${id}`} content={tooltip} />
    </>
  );

  const presetRow = (preset: FieldPreset) => {
    const on = !!presetColumnId(draft, preset.members, isAllowed);
    const takes = presetTakesFrom(draft, preset.members, isAllowed);
    return (
      <div key={`preset-${preset.key}`} className={clsx(row, 'px-3 py-3 gap-3')} data-testid={`column-option-preset-${preset.key}`}>
        {ringilMark(`ringil-preset-${preset.key}`, tr('TableViews.presetHint', 'Ringil recommended column — switching it on adds all these fields as one column.'))}
        <span className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="truncate">{tr(preset.header, preset.headerDefault)}</span>
          <span className="text-xs font-normal text-content-secondary">{preset.members.map(label).join(' \u00b7 ')}</span>
          {takes.map(taken => {
            const from = shownColumns.find(column => column.id === taken.columnId);
            return (
              <span key={taken.columnId} className="text-xs font-normal text-content-brand" data-testid={`preset-takes-${preset.key}-${taken.columnId}`}>
                {tr('TableViews.presetTakesFrom', 'Switching on moves {{fields}} from {{column}}', {
                  fields: taken.fields.map(label).join(', '),
                  column: from ? columnHeading(from, byKey, shownPresets, tr) : '',
                })}
              </span>
            );
          })}
        </span>
        <InputSwitch checked={on} onChange={e => setDraft(current => togglePreset(current, preset.members, !!e.value, isAllowed))} />
      </div>
    );
  };

  const switchRow = (key: string, text: string, checked: boolean, onChange: (on: boolean) => void, options: { disabled?: boolean; dragKey?: FieldKey } = {}) => (
    <div
      key={key}
      className={clsx(row, 'justify-between px-3 py-3', options.dragKey && !options.disabled && 'cursor-grab')}
      draggable={!!options.dragKey && !options.disabled}
      onDragStart={options.dragKey ? event => dnd.startFieldDrag(event, options.dragKey) : undefined}
      onDragEnd={dnd.endDrag}
      data-testid={`column-option-${key}`}
    >
      <span className="truncate">{text}</span>
      <InputSwitch checked={checked} disabled={options.disabled} onChange={e => onChange(!!e.value)} />
    </div>
  );

  return (
    <RingilSidebar
      visible={visible}
      heading={tr('TableViews.columnsHeading', 'Table content')}
      continueLabel={tr('generic.save', 'Save')}
      onContinue={() => {
        onApply(draft);
        if (paging && pagingDraft !== paging.mode) paging.onApply(pagingDraft);
        onClose();
      }}
      onBack={onClose}
      showSaveButton
      showBackButton
      maxWidth="550px"
    >
      <div className="flex flex-col gap-6 font-['DM_Sans']">
        {paging && (
          <section className="flex flex-col gap-2">
            <h3 className="m-0 text-sm font-medium text-content-primary">{tr('TableViews.rows', 'Rows')}</h3>
            <InputSelect
              model={[
                { code: 'pages', label: tr('TableViews.rowsPages', 'Pages') },
                { code: 'scroll', label: tr('TableViews.rowsScroll', 'Infinite scroll') },
              ]}
              selectedItemCode={pagingDraft}
              onSelect={code => setPagingDraft(code as PagingMode)}
              showCheckIcon={false}
              dataTestIdPrefix="table-paging-mode"
            />
            {pagingDraft === 'scroll' && (
              <p className="m-0 text-xs text-content-secondary">
                {tr('TableViews.rowsScrollHint', 'Rows load as you scroll, up to {{cap}}. Narrow the filters to see the rest.', { cap: paging.cap })}
              </p>
            )}
          </section>
        )}
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <h3 className="m-0 text-sm font-medium text-content-primary">{tr('TableViews.columnOrder', 'Column order')}</h3>
            <RingilButton
              mode="link"
              label={tr('TableViews.resetColumns', 'Reset to default')}
              onClick={() => {
                setRenaming(null);
                setDraft(defaultColumns());
              }}
              dataTestId="column-reset-defaults"
            />
          </div>
          {gap(0)}
          {shownColumns.map((column, index) => {
            const stacked = column.members.length > 1;
            const holdsRequired = column.members.some(isRequired);
            const preset = matchingPreset(column, shownPresets);
            return (
              <React.Fragment key={column.id}>
                <div
                  draggable
                  onDragStart={event => dnd.startColumnDrag(event, column.id, stacked ? undefined : column.members[0])}
                  onDragEnd={dnd.endDrag}
                  onDragOver={event => dnd.hoverColumn(event, column.id, column.members.length, index)}
                  onDrop={onDrop}
                  className={clsx(
                    'flex flex-col gap-1 rounded border border-solid transition-colors',
                    dnd.memberTarget?.columnId === column.id ? 'border-content-brand' : 'border-transparent',
                    dnd.draggedColumn === column.id && 'opacity-50',
                  )}
                  data-testid={`column-card-${column.id}`}
                >
                  <div className={clsx(row, 'cursor-grab')}>
                    <SvgGrip className="w-4 h-4 shrink-0 text-content-brand" />
                    {preset &&
                      ringilMark(`ringil-column-${column.id}`, tr('TableViews.presetColumnHint', 'Ringil recommended column. Changing its fields makes it your own.'))}
                    {renaming?.id === column.id ? (
                      <input
                        autoFocus
                        value={renaming.text}
                        maxLength={MAX_COLUMN_TITLE_LENGTH}
                        // An empty field means "the automatic heading", which the placeholder shows.
                        placeholder={derivedColumnHeading(column, byKey, shownPresets, tr)}
                        onChange={e => setRenaming({ id: column.id, text: e.target.value })}
                        onKeyDown={e => (e.key === 'Enter' ? commitRename() : e.key === 'Escape' ? setRenaming(null) : undefined)}
                        onBlur={commitRename}
                        // A text field inside a draggable card would otherwise start a drag on text selection.
                        draggable
                        onDragStart={e => e.preventDefault()}
                        className="min-w-0 flex-1 h-6 px-2 rounded border border-solid border-content-brand bg-base-secondary font-[inherit] text-sm text-content-primary outline-none"
                        aria-label={tr('generic.title', 'Name')}
                        data-testid={`column-title-input-${column.id}`}
                      />
                    ) : (
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate">{columnHeading(column, byKey, shownPresets, tr)}</span>
                        {/* A renamed single-field column says which field it is; a stack lists its fields below anyway. */}
                        {column.title && !stacked && (
                          <span className="truncate text-xs font-normal text-content-secondary" data-testid={`column-original-${column.id}`}>
                            {derivedColumnHeading(column, byKey, shownPresets, tr)}
                          </span>
                        )}
                      </span>
                    )}
                    {renaming?.id !== column.id && (
                      <button
                        type="button"
                        onClick={() => setRenaming({ id: column.id, text: column.title ?? '' })}
                        aria-label={tr('generic.rename', 'Rename')}
                        title={tr('generic.rename', 'Rename')}
                        className="flex p-0 bg-transparent border-0 cursor-pointer text-content-tertiary hover:text-content-brand"
                        data-testid={`column-rename-${column.id}`}
                      >
                        <SvgPencil className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {removeButton(
                      () => setDraft(current => removeColumnFromSidebar(current, column.id, isAllowed)),
                      holdsRequired,
                      holdsRequired ? tr('validation.required', 'Value is required') : tr('generic.remove', 'Remove'),
                    )}
                  </div>
                  {stacked &&
                    column.members.map((key, memberIndex) => (
                      <div
                        key={key}
                        draggable
                        onDragStart={event => dnd.startFieldDrag(event, key)}
                        onDragEnd={dnd.endDrag}
                        onDragOver={event => dnd.hoverMember(event, column.id, memberIndex)}
                        onDrop={onDrop}
                        className={clsx(
                          row,
                          'ml-6 py-1.5 font-normal cursor-grab border-0 border-solid',
                          dnd.memberTarget?.columnId === column.id && dnd.memberTarget.index === memberIndex && 'border-t-2 border-content-brand',
                          dnd.draggedKey === key && 'opacity-50',
                        )}
                        data-testid={`column-member-${key}`}
                      >
                        <SvgGrip className="w-3.5 h-3.5 shrink-0 text-content-tertiary" />
                        <span className="truncate">{label(key)}</span>
                        {removeButton(
                          () => setDraft(current => removeField(current, key)),
                          isRequired(key),
                          isRequired(key) ? tr('validation.required', 'Value is required') : tr('generic.remove', 'Remove'),
                        )}
                      </div>
                    ))}
                </div>
                {gap(index + 1)}
              </React.Fragment>
            );
          })}
          <p className="m-0 text-xs text-content-secondary">
            {tr('TableViews.columnsHint', 'Drag a field onto a column to stack them, or between columns for a new column.')}
          </p>
        </section>

        <section className="flex flex-col gap-2">
          <h3 className="m-0 text-sm font-medium text-content-primary">{tr('TableViews.availableColumns', 'Available columns')}</h3>
          {groups.map(group => {
            const groupPresets = shownPresets.filter(preset => preset.group === group.key);
            const groupFields = fields.filter(field => field.group === group.key && (isAllowed(field.key) || securityMode));
            if (groupPresets.length === 0 && groupFields.length === 0) return null;
            return (
              <div key={group.key} className="flex flex-col gap-px">
                <span className="pt-2 pb-1 text-xs font-medium uppercase tracking-wide text-content-secondary">{tr(group.key, group.fallback)}</span>
                {groupPresets.map(presetRow)}
                {groupFields.map(field => {
                  const allowed = isAllowed(field.key);
                  const option = switchRow(
                    field.key,
                    tr(field.header, field.headerDefault),
                    isFieldShown(draft, field.key),
                    on => setDraft(current => toggleField(current, field.key, on)),
                    {
                      // A required field can't be switched off; a forbidden one only shows in security mode.
                      disabled: field.required || !allowed,
                      dragKey: field.key,
                    },
                  );
                  return field.secured ? (
                    <SecuredArea key={field.key} id={`table-field-${field.key}`} visible={field.secured} label={`Column: ${field.headerDefault}`}>
                      {option}
                    </SecuredArea>
                  ) : (
                    option
                  );
                })}
              </div>
            );
          })}
        </section>
      </div>
    </RingilSidebar>
  );
};

export default ColumnSidebar;
